import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Card, Badge } from '../components/ui';
import OrderPagination from '../components/OrderPagination';

interface OrderItem {
  product_id: string;
  price: number;
  quantity: number;
  product_name?: string;
}

interface OrderWithItems {
  id: string;
  status: string;
  total_price: number;
  created_at: string;
  items: OrderItem[];
  customer?: { username: string; email: string } | null;
}

const DEFAULT_LIMIT = 10;

export default function MyOrders() {
  const [orders, setOrders] = useState<OrderWithItems[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const userStr = localStorage.getItem('user');
  let user: { role?: string } | null = null;
  try {
    user = userStr ? JSON.parse(userStr) : null;
  } catch { user = null; }
  const role = String(user?.role || '').toLowerCase();
  const isAdmin = ['admin', 'owner', 'dueno', 'dueño'].includes(role);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    const url = isAdmin ? '/admin/orders' : '/orders/my';
    api.get(url, { params: { page, limit } })
      .then(r => {
        setOrders(r.data?.data ?? []);
        setTotal(r.data?.total ?? 0);
        setTotalPages(r.data?.totalPages ?? 0);
      })
      .catch(() => {
        setOrders([]);
        setTotal(0);
        setTotalPages(0);
      })
      .finally(() => setLoading(false));
  }, [isAdmin, page, limit]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handlePageChange = (newPage: number) => {
    setPage(Math.max(1, Math.min(newPage, totalPages)));
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const formatDate = (d: string) => {
    try {
      return new Date(d).toLocaleDateString('es', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch { return d; }
  };

  const statusBadge = (status: string) => {
    const v = status === 'paid' ? 'success' : status === 'pending' ? 'warning' : 'default';
    const label = status === 'paid' ? 'Pagado' : status === 'pending' ? 'Pendiente' : status;
    return <Badge variant={v}>{label}</Badge>;
  };

  const shortId = (id: string) => id ? id.slice(0, 8) : '-';

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin h-12 w-12 border-2 border-rs-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <h1 className="font-display text-2xl font-bold text-gray-100 mb-8">
        {isAdmin ? 'Ventas' : 'Mis compras'}
      </h1>

      {orders.length === 0 ? (
        <Card className="text-center py-16">
          <p className="text-gray-500 mb-6">
            {isAdmin ? 'No hay ventas registradas' : 'No tienes compras aún'}
          </p>
          <Link to="/">
            <span className="text-rs-accent hover:underline">
              {isAdmin ? 'Ir al catálogo público' : 'Ir al catálogo'}
            </span>
          </Link>
        </Card>
      ) : isAdmin ? (
        /* Admin: tabla ordenada */
        <Card padding="none" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-rs-border bg-rs-surfaceLight/50">
                  <th className="px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Orden</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Cliente</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Productos</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-rs-border hover:bg-rs-surfaceLight/30 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm text-rs-accent">#{shortId(o.id)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-gray-200 font-medium">{o.customer?.username ?? '-'}</p>
                        <p className="text-gray-500 text-sm">{o.customer?.email ?? '-'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm whitespace-nowrap">{formatDate(o.created_at)}</td>
                    <td className="px-6 py-4 font-display font-semibold text-rs-accent">${Number(o.total_price).toFixed(2)}</td>
                    <td className="px-6 py-4">{statusBadge(o.status)}</td>
                    <td className="px-6 py-4">
                      <ul className="space-y-1 text-sm text-gray-400">
                        {o.items?.map((it, i) => (
                          <li key={i}>
                            {it.quantity}x {it.product_name || `Producto #${shortId(it.product_id)}`} — ${Number(it.price).toFixed(2)}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <OrderPagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
          />
        </Card>
      ) : (
        /* Cliente: cards organizadas con paginación */
        <Card padding="none" className="overflow-hidden">
        <div className="p-4 sm:p-6 space-y-6">
          {orders.map((o) => (
            <Card key={o.id} padding="lg" className="border border-rs-border">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <span className="font-mono text-sm text-rs-accent">Orden #{shortId(o.id)}</span>
                    {statusBadge(o.status)}
                    <span className="text-gray-500 text-sm">{formatDate(o.created_at)}</span>
                  </div>
                  <div className="space-y-3">
                    {o.items && o.items.length > 0 && (
                      <div className="rounded-lg bg-rs-surfaceLight/50 border border-rs-border p-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Productos</p>
                        <ul className="space-y-2">
                          {o.items.map((it, i) => (
                            <li key={i} className="flex justify-between items-center text-sm">
                              <span className="text-gray-300">
                                {it.quantity}x {it.product_name || `Producto #${shortId(it.product_id)}`}
                              </span>
                              <span className="text-gray-400">${Number(it.price).toFixed(2)} c/u</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 md:text-right">
                  <p className="text-2xl font-display font-bold text-rs-accent">${Number(o.total_price).toFixed(2)}</p>
                  <p className="text-gray-500 text-sm mt-1">Total</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
        <OrderPagination
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
        />
        </Card>
      )}
    </div>
  );
}
